// app/frontend/components/Cards/VideoCard.tsx

import React from 'react';
import { Card, CardSection, Image, Badge, Text, Button, Skeleton, Avatar, Group } from '@mantine/core';

interface RelatedEntity {
  name: string;
  avatar_url?: string;
}

export interface Video {
  id: number;
  title: string;
  description?: string;
  youtube_url: string;
  video_type: string;
  thumbnail_url: string;
  event?: { name: string } | null;
  organizers?: RelatedEntity[];
  artists?: RelatedEntity[]; // now with avatar_url
  dance_styles?: RelatedEntity[];
}

interface VideoCardProps {
  video: Video;
  onWatch: (video: Video) => void;
}

const VideoCard: React.FC<VideoCardProps> = ({ video, onWatch }) => {
  return (
    <Card
      shadow="sm"
      p="md"
      radius="md"
      onClick={() => onWatch(video)}
      withBorder
      style={{
        backgroundColor: '#17171750',
        borderColor: '#5c5c5c',
        overflow: 'hidden',
      }}
    >
      {/* Thumbnail + badge overlay */}
      <CardSection
        style={{
          position: 'relative',
          paddingBottom: '56.25%',
          height: 0,
          overflow: 'hidden',
        }}
      >
        <Image
          src={video.thumbnail_url}
          alt={video.title}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
        />
        <Badge variant="light" color="white" style={{ position: 'absolute', top: 8, left: 8, zIndex: 1 }}>
          {video.video_type}
        </Badge>
      </CardSection>

      {/* Title (clamped) */}
      <Text
        component="h5"
        mt="md"
        fw={700}
        lineClamp={2}
        className="poppins"
        style={{ color: '#f2f2f2', lineHeight: 1.2 }}
      >
        {video.title}
      </Text>

      {/* Dance style badges */}
      {(video.dance_styles ?? []).length > 0 && (
        <Group gap="xs" mt="xs">
          {(video.dance_styles ?? []).map((style) => (
            <Badge key={style.id} color="#EA580C" size="xs" className="tag-badge">
              {style.name}
            </Badge>
          ))}
        </Group>
      )}

      {/* Artist avatars */}
      {video.artists && video.artists.length > 0 && (
        <Group gap="sm" mt="xs">
          {video.artists.map((artist) => (
            <Avatar key={artist.name} src={artist.avatar_url} alt={artist.name} radius="xl" size="md" />
          ))}
        </Group>
      )}
    </Card>
  );
};

export default VideoCard;
